import React, {
  FC, useEffect, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/src/styles.scss';

import { useLocale } from 'lib';
import { alarmDetailsSelector } from 'features/details/exports';
import { loadFirstMomentRecordings, FirstMomentRecordingFile } from './loadRecordings';
import '../recording/patch.scss';
import styles from '../recording/style.module.scss';

const pollIntervalMs = 5000;
const maxFailures = 5;

type PlayableRecording = Pick<FirstMomentRecordingFile, 'recordingId' | 'version'> & { url: string };

/**
 * Plays one recording. A new chunk gives a new `url`; the player then continues where the operator was
 * (position and play state) instead of starting over.
 */
const RecordingPlayer: FC<{ url: string }> = ({ url }) => {

  const playerRef = useRef<AudioPlayer>(null);
  const shownUrl = useRef(url);
  const resume = useRef({ time: 0, playing: false });

  // Still the old source in the player here: remember where it is before it is replaced.
  if (shownUrl.current !== url) {

    const audio = playerRef.current?.audio.current;

    resume.current = { time: audio?.currentTime ?? 0, playing: !!audio && !audio.paused };
    shownUrl.current = url;

  }

  const restore = () => {

    const audio = playerRef.current?.audio.current;
    const { time, playing } = resume.current;

    resume.current = { time: 0, playing: false };

    if (!audio || (time === 0 && !playing)) return;

    audio.currentTime = time;

    if (playing) audio.play().catch(() => undefined);

  };

  return (
    <AudioPlayer
      layout={'horizontal-reverse'}
      ref={playerRef}
      className={styles.audio}
      src={url}
      showJumpControls={false}
      customAdditionalControls={[]}
      customVolumeControls={[]}
      autoPlayAfterSrcChange={false}
      onLoadedData={restore}
    />
  );

};

/**
 * The audio the app recorded from the alarm button press until somebody joined the call. It is shown where
 * the legacy recording is shown, and polls while the call is open so new chunks are added.
 */
const FirstMomentRecording: FC = () => {

  const { t } = useLocale();
  const alarm = useSelector(alarmDetailsSelector);
  const alarmId = alarm?._id;
  const alarmCreatedAt = alarm?.createdAt;
  const [recordings, setRecordings] = useState<PlayableRecording[]>([]);

  useEffect(() => {

    if (!alarmId || !alarmCreatedAt) return undefined;

    const controller = new AbortController();
    const cache = new Map<string, ArrayBuffer>();
    const urls = new Map<string, PlayableRecording>();
    let timer: number | undefined;
    let failures = 0;

    const toPlayable = (recording: FirstMomentRecordingFile): PlayableRecording => {

      const known = urls.get(recording.recordingId);

      if (known?.version === recording.version) return known;

      if (known) URL.revokeObjectURL(known.url);

      const playable = { ...recording, url: URL.createObjectURL(recording.blob) };

      urls.set(recording.recordingId, playable);

      return playable;

    };

    const load = async () => {

      let keepPolling = true;

      try {

        const result = await loadFirstMomentRecordings(alarmId, alarmCreatedAt, cache, controller.signal);

        if (controller.signal.aborted) return;

        keepPolling = result.keepPolling;
        failures = 0;
        setRecordings(result.recordings.map(toPlayable));

      } catch (error) {

        if (controller.signal.aborted) return;

        // Try again on the next poll, but not forever (e.g. a download that is blocked or forbidden).
        failures += 1;

      }

      if (keepPolling && failures < maxFailures) timer = window.setTimeout(load, pollIntervalMs);

    };

    load();

    return () => {

      controller.abort();
      window.clearTimeout(timer);
      urls.forEach(({ url }) => URL.revokeObjectURL(url));

    };

  }, [alarmId, alarmCreatedAt]);

  if (recordings.length === 0) return null;

  return (
    <>
      {recordings.map(({ recordingId, url }) => (
        <div key={recordingId} className={styles.recording}>
          <span className={styles.title}>
            {t('Alarm_Detail_DCC.Recording.Title')}
          </span>
          <span className={styles.sub}>
            {t('Alarm_Detail_DCC.Recording.Instruction')}
          </span>
          <RecordingPlayer url={url}/>
        </div>
      ))}
    </>
  );

};

export default FirstMomentRecording;
